/**
 * PlanFormDialog — Create / Edit Purchase Plan.
 *
 * Access reference: `frmPurchaseQryLocalP_Plan_Add` (Caption "Edit - Purchase
 * Plan"). Layout port (by `Top` twips of the spec):
 *   Row 0 (Top=48):    [P Contract dropdown | NPE Contract readonly]
 *   Row 1 (Top=420):   [Supplier Name ro   | Contract Date ro]
 *   Row 2 (Top=828):   [Delivery Latest ro | To be Received ro]
 *   Row 3 (Top=1236):  [Quality ro          | Quantity ro]
 *   Row 4 (Top=1644):  Label "1. Edit Purchase Plan" + btnSave2 (Save & Reload)
 *   Row 5 (Top=2088):  subForm = frmPurchaseQryLocalP_Plan_subForm1
 *                      (plan_date, plan_quantity, note)
 *   Row 6 (Top=5556):  Label "2. Plan Result"
 *   Row 7 (Top=5916):  subForm2 — list of plans for the selected contract.
 *
 * The Plan record itself stores only (contract_no, check_date, plan_quant,
 * note); the 6 contract-detail textboxes are read-only auto-fills, mirroring
 * Access AfterUpdate of `p_contract`. NPE Contract pulls `npe_ref` of the
 * selected P contract (Access RowSource `public_v_consignment_p` is for
 * display lookup only — Plan doesn't persist this).
 */
import { yupResolver } from '@hookform/resolvers/yup'
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import dayjs from 'dayjs'
import { useEffect, useMemo, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import * as yup from 'yup'

import ConfirmDialog from '@/components/ConfirmDialog'
import { usePagePermission } from '@/hooks/usePagePermission'
import { useSnackbar } from '@/hooks/useSnackbar'
import { useListLocalPQuery } from '@/store/api/purchaseContractApi'
import {
  useCreatePlanMutation,
  useDeletePlanMutation,
  useListPlansQuery,
  useUpdatePlanMutation,
} from '@/store/api/purchaseContractApi'
import type {
  LocalPListItem,
  LocalPPlanCreateIn,
  LocalPPlanListItem,
  LocalPPlanOut,
} from '@/types/api'
import { getErrorMessage } from '@/utils/error'

import { HIDE_ON_MOBILE_INLINE } from '@/theme/responsive'

import {
  compactDialogContentSx,
  dialogHeaderBtnSx,
  dialogTitleActionsSx,
  dialogTitleSx,
  dialogTitleTextSx,
  numSx,
  roSx,
} from './dialogStyle'

export interface PlanFormDialogProps {
  open: boolean
  mode: 'create' | 'edit'
  fixedContractNo?: string
  initialValue?: LocalPPlanOut | null
  onClose: () => void
  onSaved?: () => void
}

interface PlanFormValues {
  contract_no: string
  check_date: string
  plan_quant: number | ''
  note: string
}

const EMPTY: PlanFormValues = {
  contract_no: '',
  check_date: dayjs().format('YYYY-MM-DD'),
  plan_quant: '',
  note: '',
}

const schema: yup.ObjectSchema<PlanFormValues> = yup.object({
  contract_no: yup.string().trim().required('Contract is required'),
  check_date: yup.string().required('Plan Date is required'),
  plan_quant: yup
    .mixed<number | ''>()
    // Field lưu chuỗi raw ("12.5") trong lúc gõ → coerce sang number trước khi
    // test (giá trị submit cũng là number qua yupResolver).
    .transform((_value, originalValue) => {
      if (originalValue === '' || originalValue === null || originalValue === undefined) return ''
      const n = Number(originalValue)
      return Number.isFinite(n) ? n : originalValue
    })
    .test('required', 'Quantity is required', (v) => v !== '' && v !== undefined && v !== null)
    .test('positive', 'Quantity must be > 0', (v) => typeof v === 'number' && v > 0)
    .required(),
  note: yup.string().trim().default(''),
}) as yup.ObjectSchema<PlanFormValues>

function fromPlan(p: LocalPPlanOut): PlanFormValues {
  return {
    contract_no: p.contract_no ?? '',
    check_date: p.check_date ?? '',
    plan_quant: p.plan_quant ?? '',
    note: p.note ?? '',
  }
}

function toApi(v: PlanFormValues): LocalPPlanCreateIn {
  return {
    contract_no: v.contract_no.trim(),
    check_date: v.check_date,
    plan_quant: typeof v.plan_quant === 'number' ? v.plan_quant : 0,
    note: v.note.trim() === '' ? null : v.note.trim(),
  }
}

const ACCESS_MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
const fmtAccessDate = (v: unknown): string => {
  if (!v) return ''
  const d = new Date(String(v))
  if (Number.isNaN(d.getTime())) return String(v)
  return `${`${d.getDate()}`.padStart(2, '0')}-${ACCESS_MONTHS[d.getMonth()]}-${d.getFullYear()}`
}
const fmtStandard = (v: number | null | undefined): string =>
  v == null ? '' : v.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })

/** Access subform `frmPurchaseQryLocalP_Plan_subForm2` columns. `id` được
 * thêm ở đầu (Access không show id nhưng FE cần để double-click load
 * Edit + per-row Delete). Rows estimate-only (id=null) không edit/delete được. */
const PLAN_LIST_COLUMNS: Array<{
  key: keyof LocalPPlanListItem
  label: string
  align?: 'right'
  kind?: 'date' | 'number'
}> = [
  { key: 'id', label: 'ID', align: 'right' },
  { key: 'check_date', label: 'Estimated Date', kind: 'date' },
  { key: 'week_of_year', label: 'Week Of Year', align: 'right' },
  { key: 'check_type', label: 'Estimated Type' },
  { key: 'estimate_for_date', label: 'Estimated Quantity', align: 'right', kind: 'number' },
  { key: 'plan_quant', label: 'Trader Plan', align: 'right', kind: 'number' },
  { key: 'alc_quant', label: 'Traffic Plan', align: 'right', kind: 'number' },
  { key: 'contract_quant', label: 'Contract Quantity', align: 'right', kind: 'number' },
  { key: 'quantity_kg', label: 'Quantity Kg', align: 'right', kind: 'number' },
  { key: 'note', label: 'Note' },
]

export function PlanFormDialog({
  open,
  mode,
  fixedContractNo,
  initialValue,
  onClose,
  onSaved,
}: PlanFormDialogProps) {
  const snack = useSnackbar()
  const { canEdit } = usePagePermission()

  // `editingId` = plan đang được load vào subForm1 để Edit. Khởi tạo từ prop
  // (parent mở dialog ở chế độ Edit), sau đó double-click row trong Plan
  // Result sẽ ghi đè state này → tự chuyển dialog sang Edit theo ID đó.
  const [editingId, setEditingId] = useState<number | null>(
    mode === 'edit' && initialValue ? initialValue.id : null,
  )
  const isEdit = editingId != null

  const [createPlan, { isLoading: creating }] = useCreatePlanMutation()
  const [updatePlan, { isLoading: updating }] = useUpdatePlanMutation()
  const [deletePlan, { isLoading: deleting }] = useDeletePlanMutation()
  const loading = creating || updating || deleting

  const [pendingDelete, setPendingDelete] = useState<LocalPPlanListItem | null>(null)

  // `refetchOnMountOrArgChange: true` — 6 readonly display field (đặc biệt
  // `pending`) đến từ MV `public_mv_local_p` aggregation, đổi khi user khác vừa
  // thêm Plan / Fixation / Payment. Force fresh mỗi lần dialog mount để Plan
  // Date duplicate check + display panel không hiển thị giá trị cũ.
  const { data: contractsList } = useListLocalPQuery(
    {
      is_status: 1,
      page: 1,
      page_size: 1_000_000,
    },
    { refetchOnMountOrArgChange: true },
  )
  const contractOptions: LocalPListItem[] = useMemo(
    () => contractsList?.items ?? [],
    [contractsList],
  )

  const {
    control,
    handleSubmit,
    reset,
    watch,
    formState: { errors },
  } = useForm<PlanFormValues>({
    resolver: yupResolver(schema),
    defaultValues: EMPTY,
  })

  useEffect(() => {
    if (!open) return
    // Reset editingId mỗi lần dialog mở để khớp prop của parent.
    setEditingId(mode === 'edit' && initialValue ? initialValue.id : null)
    setPendingDelete(null)
    if (mode === 'edit' && initialValue) {
      reset(fromPlan(initialValue))
    } else {
      reset({ ...EMPTY, contract_no: fixedContractNo ?? '' })
    }
  }, [open, mode, initialValue, fixedContractNo, reset])

  const selectedContractNo = watch('contract_no')

  // Selected contract → drives the 6 read-only contract-detail textboxes
  // (Access `p_contract` AfterUpdate handler auto-fills supplier_name,
  // contract_date, quality, quantity, latest_delivery, pending + npe_contract).
  const selectedContract: LocalPListItem | null = useMemo(() => {
    if (!selectedContractNo) return null
    return contractOptions.find((c) => c.contract_no === selectedContractNo) ?? null
  }, [contractOptions, selectedContractNo])

  // subForm2 list — plans of the currently-selected contract. Skip when no
  // contract chosen yet (avoid fetching full list).
  const { data: planList, isFetching: planListLoading } = useListPlansQuery(
    selectedContractNo
      ? { contract_no: selectedContractNo, page: 1, page_size: 1_000_000 }
      : { page: 1, page_size: 1_000_000 },
    { skip: !open || !selectedContractNo },
  )
  const planRows: LocalPPlanListItem[] = useMemo(() => planList?.items ?? [], [planList])

  // Uniqueness constraint: 1 Plan / (contract_no, check_date). Tập các ngày
  // đã có plan cho contract đang chọn (trừ chính plan đang edit). Dùng để
  // tô đỏ ô Plan Date khi New/Edit có ngày trùng với row khác.
  const takenDates = useMemo(() => {
    const set = new Set<string>()
    for (const p of planRows) {
      if (p.id == null) continue // skip estimate rows (chưa có Plan record)
      if (editingId != null && p.id === editingId) continue // bỏ chính mình
      if (p.check_date) set.add(p.check_date)
    }
    return set
  }, [planRows, editingId])

  const currentCheckDate = watch('check_date')
  const isDateDuplicate = !!currentCheckDate && takenDates.has(currentCheckDate)

  const submit = handleSubmit(
    async (values) => {
      // Guard duplicate (contract_no, date) ở FE — BE cũng nên enforce.
      if (takenDates.has(values.check_date)) {
        snack.error(`Đã có plan cho contract '${values.contract_no}' vào ngày này.`)
        return
      }
      const payload = toApi(values)
      try {
        if (editingId != null) {
          await updatePlan({ planId: editingId, body: payload }).unwrap()
          snack.success('Plan updated')
        } else {
          await createPlan(payload).unwrap()
          snack.success('Plan created')
        }
        onSaved?.()
        onClose()
      } catch (err) {
        snack.error(
          getErrorMessage(err, editingId != null ? 'Failed to update plan' : 'Failed to create plan'),
        )
      }
    },
    () => snack.error('Please fix the highlighted fields'),
  )

  // Access btnSave2 "Save && Reload" — save current plan, then reset the
  // plan-input fields but keep the same contract selected so user can enter
  // another plan row. Edit mode có hành vi tương tự: save xong reset về New
  // cho contract đang chọn.
  const submitAndReload = handleSubmit(
    async (values) => {
      if (takenDates.has(values.check_date)) {
        snack.error(`Đã có plan cho contract '${values.contract_no}' vào ngày này.`)
        return
      }
      const payload = toApi(values)
      try {
        if (editingId != null) {
          await updatePlan({ planId: editingId, body: payload }).unwrap()
          snack.success('Plan updated')
        } else {
          await createPlan(payload).unwrap()
          snack.success('Plan created')
        }
        onSaved?.()
        setEditingId(null)
        // Dùng `reset` (thay vì setValue lẻ) để Controller DatePicker chắc
        // chắn pick up giá trị mới — setValue đôi khi không trigger rerender
        // của DatePicker khi state cha cũng đổi cùng tick.
        reset({
          contract_no: values.contract_no,
          check_date: dayjs().format('YYYY-MM-DD'),
          plan_quant: '',
          note: '',
        })
      } catch (err) {
        snack.error(getErrorMessage(err, 'Failed to save plan'))
      }
    },
    () => snack.error('Please fix the highlighted fields'),
  )

  // Double-click 1 row trong Plan Result → Edit **theo ID**, không theo
  // ngày: dùng `row.id` làm khóa duy nhất để tra lại record trong
  // `planRows` và nạp vào subForm1. Bỏ qua row estimate (id=null).
  const loadRowForEdit = (row: LocalPPlanListItem) => {
    if (row.id == null) {
      snack.warning('This row is an estimate — no Plan record to edit yet.')
      return
    }
    const planId = row.id
    const planById = planRows.find((p) => p.id === planId)
    if (!planById) {
      snack.warning(`Plan ID #${planId} not found in the list.`)
      return
    }
    setEditingId(planId)
    reset({
      contract_no: planById.contract_no,
      check_date: planById.check_date ?? dayjs().format('YYYY-MM-DD'),
      plan_quant: planById.plan_quant ?? '',
      note: planById.note ?? '',
    })
  }

  // Reset subForm1 về trạng thái New, giữ contract đã chọn.
  const clearEditState = () => {
    setEditingId(null)
    reset({
      contract_no: selectedContractNo ?? '',
      check_date: dayjs().format('YYYY-MM-DD'),
      plan_quant: '',
      note: '',
    })
  }

  const confirmDelete = async () => {
    if (!pendingDelete?.id) return
    const targetId = pendingDelete.id
    try {
      await deletePlan(targetId).unwrap()
      snack.success('Plan deleted')
      onSaved?.()
      if (editingId === targetId) clearEditState()
      setPendingDelete(null)
    } catch (err) {
      snack.error(getErrorMessage(err, 'Failed to delete plan'))
    }
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      fullWidth
      scroll="paper"
      PaperProps={{ sx: { width: '96vw', maxWidth: 1600 } }}
    >
      <DialogTitle sx={dialogTitleSx}>
        <Typography
          component="span"
          noWrap
          title={isEdit ? 'Edit - Purchase Plan' : 'New - Purchase Plan'}
          sx={dialogTitleTextSx}
        >
          {isEdit ? 'Edit' : 'New'}
          <Box component="span" sx={HIDE_ON_MOBILE_INLINE}> - Purchase Plan</Box>
        </Typography>
        <Stack direction="row" spacing={0.5} sx={dialogTitleActionsSx}>
          <Button
            variant="contained"
            size="small"
            color="success"
            onClick={() => void submit()}
            disabled={loading || isDateDuplicate || !canEdit}
            sx={dialogHeaderBtnSx}
          >
            {loading ? <CircularProgress size={14} color="inherit" /> : 'Save'}
          </Button>
          <Button variant="outlined" size="small" onClick={onClose} disabled={loading} sx={dialogHeaderBtnSx}>
            Cancel
          </Button>
        </Stack>
      </DialogTitle>
      <Divider />
      <DialogContent dividers sx={compactDialogContentSx}>
        {/* Top 4-row x 2-col contract info grid — Access spec rows by Top
            twips (48 / 420 / 828 / 1236). */}
        <Grid container spacing={1.25} sx={{ mt: 0 }}>
          <Grid item xs={12} sm={6}>
            {/* P Contract luôn enabled (yêu cầu chị Đẹp): cả create lẫn edit
                mode đều cho phép đổi contract. `fixedContractNo` chỉ còn vai
                trò pre-fill giá trị ban đầu (xử lý trong useEffect reset). */}
            <Controller
              control={control}
              name="contract_no"
              render={({ field }) => {
                const selected =
                  contractOptions.find((c) => c.contract_no === field.value) ??
                  (field.value
                    ? ({
                        contract_no: field.value,
                        supplier_name: null,
                        supplier_code: null,
                      } as LocalPListItem)
                    : null)
                return (
                  <Autocomplete
                    size="small"
                    options={contractOptions}
                    value={selected}
                    onChange={(_e, v) => field.onChange(v?.contract_no ?? '')}
                    // codeOnly: ô input chỉ hiện contract_no (đã có ô Supplier
                    // Name riêng); dropdown vẫn đầy đủ "<contract_no> — <supplier_code> - <supplier_name>".
                    getOptionLabel={(opt) => (opt ? String(opt.contract_no ?? '') : '')}
                    renderOption={(liProps, opt) => (
                      <li {...liProps} key={opt.contract_no}>
                        {`${opt.contract_no} — ${opt.supplier_code ?? ''}${opt.supplier_code && opt.supplier_name ? ' - ' : ''}${opt.supplier_name ?? ''}`}
                      </li>
                    )}
                    isOptionEqualToValue={(o, v) => o.contract_no === v.contract_no}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="P Contract *"
                        error={!!errors.contract_no}
                        helperText={errors.contract_no?.message}
                      />
                    )}
                  />
                )
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="NPE Contract"
              size="small"
              value={selectedContract?.npe_ref ?? ''}
              InputProps={{ readOnly: true }}
              InputLabelProps={{ shrink: true }}
              fullWidth
              sx={roSx}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              label="Supplier Name"
              size="small"
              value={selectedContract?.supplier_name ?? ''}
              InputProps={{ readOnly: true }}
              InputLabelProps={{ shrink: true }}
              fullWidth
              sx={roSx}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Contract Date"
              size="small"
              value={fmtAccessDate(selectedContract?.contract_date)}
              InputProps={{ readOnly: true }}
              InputLabelProps={{ shrink: true }}
              fullWidth
              sx={roSx}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              label="Delivery Latest"
              size="small"
              value={fmtAccessDate(selectedContract?.latest_delivery)}
              InputProps={{ readOnly: true }}
              InputLabelProps={{ shrink: true }}
              fullWidth
              sx={roSx}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="To be Received"
              size="small"
              value={fmtStandard(selectedContract?.pending)}
              InputProps={{ readOnly: true }}
              InputLabelProps={{ shrink: true }}
              fullWidth
              sx={{ ...roSx, ...numSx }}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              label="Quality"
              size="small"
              value={selectedContract?.quality_code ?? ''}
              InputProps={{ readOnly: true }}
              InputLabelProps={{ shrink: true }}
              fullWidth
              sx={roSx}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Quantity"
              size="small"
              value={fmtStandard(selectedContract?.quantity_kg)}
              InputProps={{ readOnly: true }}
              InputLabelProps={{ shrink: true }}
              fullWidth
              sx={{ ...roSx, ...numSx }}
            />
          </Grid>
        </Grid>

        {/* Section "1. Edit Purchase Plan" + Save & Reload — Access Row 4
            (Top=1644). Tiêu đề đổi "Editing ID #N" khi đang edit để chị
            biết row nào đang nạp; nút "New" cho phép rời edit về create. */}
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          flexWrap="wrap"
          useFlexGap
          rowGap={0.5}
          sx={{ mt: 2, mb: 0.75 }}
          spacing={1}
        >
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
            1. Edit Purchase Plan
            {editingId != null ? ` — Editing ID #${editingId}` : ''}
          </Typography>
          <Stack direction="row" spacing={0.5}>
            {editingId != null && (
              <Button
                variant="text"
                size="small"
                onClick={clearEditState}
                disabled={loading}
                sx={dialogHeaderBtnSx}
              >
                New
              </Button>
            )}
            <Button
              variant="outlined"
              size="small"
              onClick={() => void submitAndReload()}
              disabled={loading || !selectedContractNo || isDateDuplicate || !canEdit}
              sx={dialogHeaderBtnSx}
            >
              Save & Reload
            </Button>
          </Stack>
        </Stack>
        <Divider sx={{ mb: 1 }} />

        {/* subForm = frmPurchaseQryLocalP_Plan_subForm1: plan_date,
            plan_quantity, note. */}
        <Grid container spacing={1.25}>
          <Grid item xs={12} sm={4}>
            <Controller
              control={control}
              name="check_date"
              render={({ field }) => (
                <DatePicker
                  label="Plan Date *"
                  format="DD-MMM-YYYY"
                  value={field.value ? dayjs(field.value) : null}
                  onChange={(d) => field.onChange(d && d.isValid() ? d.format('YYYY-MM-DD') : '')}
                  slotProps={{
                    textField: {
                      size: 'small',
                      fullWidth: true,
                      // Update key = (contract_no, date) → trùng date với
                      // plan khác của cùng contract → field đỏ.
                      error: !!errors.check_date || isDateDuplicate,
                      helperText:
                        errors.check_date?.message ??
                        (isDateDuplicate
                          ? 'A plan already exists for this contract on this date — choose another date.'
                          : undefined),
                    },
                  }}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <Controller
              control={control}
              name="plan_quant"
              render={({ field }) => (
                <TextField
                  label="Plan Quantity *"
                  type="text"
                  size="small"
                  fullWidth
                  value={field.value === '' || field.value == null ? '' : String(field.value)}
                  // Chỉ cho nhập SỐ (digits + 1 dấu thập phân) — type="text" +
                  // reject keystroke không khớp pattern → chặn chữ/ký hiệu trên
                  // MỌI trình duyệt (type="number" vẫn lọt chữ ở Firefox). Lưu
                  // chuỗi raw để giữ "12." đang gõ; schema transform → number.
                  onChange={(e) => {
                    const raw = e.target.value
                    if (raw !== '' && !/^\d*\.?\d*$/.test(raw)) return
                    field.onChange(raw)
                  }}
                  onBlur={field.onBlur}
                  inputProps={{ inputMode: 'decimal' }}
                  error={!!errors.plan_quant}
                  helperText={errors.plan_quant?.message}
                  sx={numSx}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <Controller
              control={control}
              name="note"
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Note"
                  size="small"
                  fullWidth
                  error={!!errors.note}
                  helperText={errors.note?.message}
                />
              )}
            />
          </Grid>
        </Grid>

        {/* Section "2. Plan Result" + subForm2 list (Access Row 6/7,
            Top=5556/5916). RecordSource ban đầu của subForm2 là query
            `PurchaseQryLocalP_PlanContract`; FE port = list plans filtered by
            contract_no. */}
        <Typography variant="subtitle2" sx={{ fontWeight: 700, mt: 2, mb: 0.75 }}>
          2. Plan Result
        </Typography>
        <Divider sx={{ mb: 1 }} />
        <TableContainer sx={{ maxHeight: 280, border: (t) => `1px solid ${t.palette.divider}` }}>
          <Table
            size="small"
            stickyHeader
            sx={{
              // Header sticky opaque (theme MuiTableHead = gold 0.18 trong suốt →
              // scroll lòi data); gradient cream-gold qua selector thắng specificity.
              '& thead .MuiTableCell-head': {
                backgroundColor: '#fff9f4',
                backgroundImage: 'linear-gradient(180deg, #fff9f4 0%, #f4df9c 100%)',
              },
            }}
          >
            <TableHead>
              <TableRow>
                <TableCell
                  align="center"
                  sx={{ fontWeight: 700, whiteSpace: 'nowrap', bgcolor: 'background.default' }}
                >
                  Action
                </TableCell>
                {PLAN_LIST_COLUMNS.map((col) => (
                  <TableCell
                    key={String(col.key)}
                    align={col.align}
                    sx={{ fontWeight: 700, whiteSpace: 'nowrap', bgcolor: 'background.default' }}
                  >
                    {col.label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {!selectedContractNo ? (
                <TableRow>
                  <TableCell colSpan={PLAN_LIST_COLUMNS.length + 1} sx={{ color: 'text.secondary' }}>
                    Chọn P Contract để xem các plan đã có.
                  </TableCell>
                </TableRow>
              ) : planListLoading && planRows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={PLAN_LIST_COLUMNS.length + 1}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CircularProgress size={14} />
                      <Typography variant="caption">Loading…</Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              ) : planRows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={PLAN_LIST_COLUMNS.length + 1} sx={{ color: 'text.secondary' }}>
                    Chưa có plan nào cho contract này.
                  </TableCell>
                </TableRow>
              ) : (
                planRows.map((row, idx) => {
                  const isEstimate = row.id == null
                  const isActive = row.id != null && row.id === editingId
                  return (
                    <TableRow
                      key={row.id != null ? `id-${row.id}` : `est-${idx}`}
                      hover
                      selected={isActive}
                      sx={{
                        cursor: isEstimate ? 'default' : 'pointer',
                        '& td': isEstimate ? { color: 'text.secondary' } : undefined,
                      }}
                      onDoubleClick={() => loadRowForEdit(row)}
                      title={isEstimate ? 'Estimate row — no Plan record to edit' : 'Double-click to edit this plan'}
                    >
                      <TableCell align="center" sx={{ whiteSpace: 'nowrap', py: 0.25 }}>
                        <IconButton
                          size="small"
                          color="error"
                          disabled={isEstimate || loading || !canEdit}
                          onClick={(e) => {
                            e.stopPropagation()
                            setPendingDelete(row)
                          }}
                          title={isEstimate ? 'Estimate row cannot be deleted' : 'Delete this plan'}
                        >
                          <DeleteOutlineIcon fontSize="inherit" />
                        </IconButton>
                      </TableCell>
                      {PLAN_LIST_COLUMNS.map((col) => {
                        const val = row[col.key]
                        let text = ''
                        if (val == null) text = ''
                        else if (col.kind === 'date') text = fmtAccessDate(val)
                        else if (col.kind === 'number' && typeof val === 'number') text = fmtStandard(val)
                        else text = String(val)
                        return (
                          <TableCell
                            key={String(col.key)}
                            align={col.align}
                            sx={{ whiteSpace: 'nowrap', py: 0.5 }}
                          >
                            {text || '-'}
                          </TableCell>
                        )
                      })}
                    </TableRow>
                  )
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </DialogContent>

      <ConfirmDialog
        open={!!pendingDelete}
        title="Delete Plan"
        message={
          pendingDelete
            ? `Do you want to delete Plan ID #${pendingDelete.id} of Contract '${pendingDelete.contract_no}' ?`
            : ''
        }
        destructive
        loading={deleting}
        onConfirm={() => void confirmDelete()}
        onCancel={() => setPendingDelete(null)}
      />
    </Dialog>
  )
}

export default PlanFormDialog
